import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import type { Activity } from '@/api/activities';
import { getFixture, type Fixture } from '@/api/fixtures';

// useActivityFixtures loads the fixture behind every `match` activity in the
// given list, keyed by activity id. Calendars use it to render a game (crest,
// both sides, home/away, score) instead of a generic row.
//
// One request per match rather than a range endpoint: a calendar page holds a
// handful of matches at most, and the per-activity key is the same one the
// activity sheet uses — so opening a match reuses the row already in cache
// instead of refetching it.
//
// Fixtures with no opponent set are dropped: a TBD match renders as a plain
// activity row, not half a scoreboard.
export function useActivityFixtures(
  token: string | null,
  slug: string,
  activities: Activity[],
): Map<string, Fixture> {
  const matchIDs = useMemo(
    () => activities.filter((a) => a.activity_type === 'match').map((a) => a.id),
    [activities],
  );
  return useQueries({
    queries: matchIDs.map((id) => ({
      queryKey: ['activity-fixture', slug, id],
      queryFn: () => getFixture(token, slug, id),
      enabled: !!token && !!slug,
    })),
    // combine collapses the per-match results into one lookup so consumers take
    // a single dependency instead of an array whose identity churns per render.
    combine: (rows) => {
      const byID = new Map<string, Fixture>();
      for (const r of rows) {
        if (r.data?.opponent_name) byID.set(r.data.activity_id, r.data);
      }
      return byID;
    },
  });
}
